import { Component, inject } from '@angular/core';
import { AsyncPipe, Location, NgClass, TitleCasePipe } from '@angular/common';
import { Job } from '../../class/job.class';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TaskEnum } from '../../enums/task.enum';
import { JobService } from '../../services/job.service';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import {
  selectJobById,
  selectJobIsCompleted,
} from '../../store/jobs/jobs.selects';
import { LucideAngularModule } from 'lucide-angular';
import { StepperComponent } from '../../components/stepper/stepper.component';
import { Task } from '../../class/task.class';
import { DomSanitizer } from '@angular/platform-browser';
import { ResponsiveService } from '../../services/reponsive.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from '../../components/boutons/button/button.component';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { ConfirmModalComponent } from '../../components/modals/confirm-modal/confirm-modal.component';
import { DownloadService } from '../../services/download.service';
import { ProgressJobCounterComponent } from '../../components/progress-job-counter/progress-job-counter.component';
import { HorizontalStepperComponent } from '../../components/horizontal-stepper/horizontal-stepper.component';
import { StatusEnum } from '../../enums/status.enum';
import { ModalService } from '../../services/modal.service';
@Component({
  selector: 'app-job',
  imports: [
    StepperComponent,
    RouterLink,
    LucideAngularModule,
    NgClass,
    ButtonComponent,
    TranslateModule,
    AsyncPipe,
    TranslateModule,
    TitleCasePipe,
    RouterLink,
    ProgressJobCounterComponent,
    HorizontalStepperComponent,
  ],
  templateUrl: './job-detail.component.html',
  styleUrl: './job-detail.component.scss',
})
export class JobDetailComponent {
  public location: Location = inject(Location);
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private jobService: JobService = inject(JobService);
  private store: Store = inject(Store);
  protected sanitizer: DomSanitizer = inject(DomSanitizer);
  private responsiveService: ResponsiveService = inject(ResponsiveService);
  public downloadService: DownloadService = inject(DownloadService);
  public modalService: ModalService = inject(ModalService);

  job$: Observable<Job | undefined> = new Observable<Job | undefined>();
  taskCount: number = 0;

  // Responsives
  smallScreen$: Observable<boolean> = this.responsiveService.small$;
  mediumScreen$: Observable<boolean> = this.responsiveService.desktop$;
  largeScreen$: Observable<boolean> = this.responsiveService.largeDesktop$;

  resultUrl: string | undefined;

  videoUrl: string = '';
  isLoading: boolean = true;
  hasError: boolean = false;
  downloadProgress: number = 0;
  videoBlob: Blob | null = null;

  videoLoading: boolean = false;
  audioLoading: boolean = false;

  subscriptions: Subscription[] = [];

  jobCompleted$: Observable<boolean> = new Observable<boolean>();

  moreInfoVisible: boolean = false;

  jobDetails: { label: string; value: string }[] = [];

  downloadVideo() {
    this.videoLoading = true;
    this.subscriptions.push(
      this.job$.subscribe((job: Job | undefined) => {
        if (job) {
          const filename = `${job.title
            .replace(/[^a-z0-9]/gi, '_')
            .toLowerCase()}_${job.id}.mp4`;
          this.downloadService.getVideoFinalStream(job.id).subscribe({
            next: (blob: Blob) => {
              this.downloadService.downloadFile(blob, filename);
              this.videoLoading = false;
            },
            error: (error) => {
              console.error(
                'Erreur lors du téléchargement de la vidéo:',
                error,
              );
              this.videoLoading = false;
            },
          });
        } else {
          console.error('Job non trouvé pour le téléchargement de la vidéo.');
          this.videoLoading = false;
        }
      }),
    );
  }

  downloadAudio() {
    this.audioLoading = true;
    this.subscriptions.push(
      this.job$.subscribe((job: Job | undefined) => {
        if (job) {
          const filename = `${job.title
            .replace(/[^a-z0-9]/gi, '_')
            .toLowerCase()}_${job.id}.wav`;
          this.jobService.getAudioFinal(job.id).subscribe({
            next: (blob: Blob) => {
              this.downloadService.downloadFile(blob, filename);
              this.audioLoading = false;
            },
            error: (error) => {
              console.error("Erreur lors du téléchargement de l'audio:", error);
              this.audioLoading = false;
            },
          });
        } else {
          console.error("Job non trouvé pour le téléchargement de l'audio.");
          this.audioLoading = false;
        }
      }),
    );
  }

  getIcon(task: Task): string {
    switch (task.status) {
      case 'ERROR':
        return 'circle-alert';
      case 'COMPLETED':
        return 'circle-check-big';
      case 'PENDING':
        return 'clock';
      default:
        return 'circle';
    }
  }

  ngOnDestroy() {
    // Nettoyer l'URL créée pour éviter les fuites mémoire
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
    if (this.videoUrl) {
      URL.revokeObjectURL(this.videoUrl);
    }
  }

  onVideoError(event: any) {
    this.hasError = true;
    console.error('Erreur lors de la lecture de la vidéo:', event);
  }

  ngOnInit(): void {
    const jobId: string = this.route.snapshot.params['jobId'];
    if (jobId) {
      this.job$ = this.store.select(selectJobById(parseInt(jobId)));
      this.jobCompleted$ = this.store.select(
        selectJobIsCompleted(parseInt(jobId)),
      );
      this.taskCount = Object.keys(TaskEnum).length / 2;
      this.job$.subscribe({
        next: (job: Job | undefined) => {
          if (job) {
            // Initialiser les détails du job pour tous les jobs, pas seulement les complétés
            this.jobDetails = [
              { label: 'job_detail.title', value: job.title },
              { label: 'job_detail.id', value: job.id.toString() },
              { label: 'job_detail.file_extention', value: job.fileExtention },
              {
                label: 'common.created_at',
                value: new Date(job.createdAt).toLocaleString(),
              },
              {
                label: 'common.updated_at',
                value: new Date(job.updatedAt ?? 0).toLocaleString(),
              },
              { label: 'job_detail.status', value: `status.${job.status}` },
              {
                label: 'job_detail.lang_from',
                value: `languages.${job.LangFrom.name}`,
              },
              {
                label: 'job_detail.lang_to',
                value: `languages.${job.LangTo.name}`,
              },
              {
                label: 'common.duration',
                value: `${Math.floor(job.duration ?? 0)} secondes`,
              },
              { label: 'common.context', value: job.context || 'N/A' },
            ];

            job.tasks.forEach((task: Task) => {
              this.jobDetails.push({
                label: task.id ? `#${task.id}` : 'N/A',
                value: `status.${task.status}`,
              });
            });

            const videoEditingTask = job.tasks.find(
              (t) =>
                job.status === 'WAITING_RESPONSE' && t.type === 'VIDEO_EDITING',
            );

            if (videoEditingTask) {
              this.router.navigate(['video-editing', videoEditingTask.id], {
                relativeTo: this.route.parent,
              });
            }
          }
        },
      });
      return;
    }
    this.router.navigate(['/dashboard/my-jobs']);
    return;
  }
}
